/**
 * "Accept and commit" confirmation for step 5 (f10.8, design D8): how many
 * rates become the cycle's, and that earlier cycles keep theirs. Every number
 * is the server's.
 */

import { Check, Info } from 'lucide-react';

import type { ExchangeRatesReview } from '@/api/client';
import { Modal } from '@/routes/reference-data/Modal';

import { refusalMessage } from './exchangeRatesView';

export function commitSummary(rates: number, cycleCode: string): string {
  return `${rates} rates for cycle ${cycleCode} · earlier cycles keep their rates`;
}

export function ExchangeRatesCommitDialog({
  review,
  cycleCode,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  review: ExchangeRatesReview;
  cycleCode: string;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { figures } = review;
  const stale = review.later_steps_becoming_stale;
  return (
    <Modal
      title="Accept and commit?"
      subtitle={`Exchange rates · ${review.batch.original_filename ?? `batch #${review.batch.batch_number}`}`}
      onClose={onClose}
      onSubmit={onConfirm}
      busy={busy}
      submitLabel="Accept and commit"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      message={refusalMessage(error)}
    >
      <p className="mb-4">{commitSummary(figures.rates_to_write, cycleCode)}</p>
      <dl className="dl cols-2 mb-4">
        <div>
          <dt>Rates written</dt>
          <dd>{figures.rates_to_write}</dd>
        </div>
        <div>
          <dt>Rates superseded</dt>
          <dd>{figures.rates_to_supersede}</dd>
        </div>
        <div>
          <dt>USD rate</dt>
          <dd className="mono">{figures.usd_rate ?? '—'}</dd>
        </div>
        <div>
          <dt>Later completed steps become stale</dt>
          <dd>{stale.length ? stale.join(', ') : 'None'}</dd>
        </div>
      </dl>
      <div className="alert alert-info">
        <Info aria-hidden="true" />
        <div className="a-body">
          The commit runs as a background job in one transaction &mdash; all of it, or nothing. Superseded rates
          are kept as history, never deleted.
        </div>
      </div>
    </Modal>
  );
}
